import Axios from 'axios';
import { env } from '../env';
import { EnginePortafoglioApiFactory } from './generated/anagrafica';
import externalApi, {
  AmlTemplateApiFactory,
  AreaFirmaApiFactory,
  AzimutLifeApiFactory,
  CambioContattiApiFactory,
  ConsoleDiAmministrazioneApiFactory,
  ProspectDocumentsApiFactory,
  FondiPensioneApiFactory,
  LoginApiFactory,
  LogoutApiFactory,
  PortafoglioApiFactory,
  RegistrazioneApiFactory,
  StatoApiFactory,
  DocumentiApiFactory,
  MarketplaceApiFactory,
  DefaultApiFactory,
  StatoQuestionariApiFactory,
  WebCollaborationApiFactory,
  FiduciariaApiFactory,
  ContattoApiFactory,
  MifidApiFactory,
  TasksApiFactory,
  SigningApiFactory,
  SignroomApiFactory,
  MediaApiFactory,
  AgentsApiFactory,
  ConfigApiFactory,
  DocumentAcceptanceApiFactory,
} from './generated/index';
import {
  PirBoxProviderApiFactory,
  PortfolioProviderApiFactory,
  RegistrationProviderApiFactory,
} from './generated/objectway-zenith';
import { DocumentContentProviderApiFactory } from './generated/papyrus-stream';
import appJson from '../../../../mobile/app.json';

const axiosInstance = Axios.create({
  timeout: env.apiTimeout,
});

let mobilePlatform = 'unknown';

export const setMobilePlatform = (platform: string) => {
  mobilePlatform = platform;
};

if (env.appModule === 'mobile') {
  axiosInstance.interceptors.request.use(
    (config) => {
      config.headers['X-App-Version'] = `v${appJson.expo.version}`;
      config.headers['User-Agent'] = `MyAzimut-mobile/${appJson.expo.version} (${mobilePlatform})`;
      return config;
    },
    (error) => Promise.reject(error)
  );
}

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url ?? 'URL not available';
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error(
        `[API Error] Request to ${url} failed with status ${error.response.status}`,
        error.response.data
      );
    } else if (error.request) {
      // The request was made but no response was received
      console.error(
        `[API Error] No response received for request to ${url}`,
        error.request
      );
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error(
        `[API Error] Error setting up request to ${url}`,
        error.message
      );
    }
    return Promise.reject(error);
  }
);

const {
  papyrusMetadata,
  anagrafica,
  dob,
  documentale,
  prometeia,
  signroom,
  anagraficaCF,
} = externalApi;
class ApiManager {
  readonly axiosInstance = axiosInstance;

  readonly loginApi = LoginApiFactory(env.apiConfig, undefined, axiosInstance);

  readonly logoutApi = LogoutApiFactory(
    env.apiConfig,
    undefined,
    axiosInstance
  );

  readonly fiduciarieApi = FiduciariaApiFactory(
    env.apiConfig,
    undefined,
    axiosInstance
  );

  readonly statusApi = StatoApiFactory(env.apiConfig, undefined, axiosInstance);

  readonly prepareChangeContact = ContattoApiFactory(
    env.apiConfig,
    undefined,
    axiosInstance
  );

  readonly registrationApi = RegistrazioneApiFactory(
    env.apiConfig,
    undefined,
    axiosInstance
  );

  readonly changeContactApi = CambioContattiApiFactory(
    env.apiConfig,
    undefined,
    axiosInstance
  );

  readonly signatureAreaApi = AreaFirmaApiFactory(
    env.apiConfig,
    undefined,
    axiosInstance
  );

  readonly signroomApi = SignroomApiFactory(
    env.apiConfig,
    undefined,
    axiosInstance
  );

  readonly complexMetadataApi =
    papyrusMetadata.ComplexMetadataProviderApiFactory(
      {
        isJsonMime: () => true,
        basePath: env.documentContentBaseUrl,
      },
      undefined,
      axiosInstance
    );

  readonly azDocumentProviderApi = papyrusMetadata.AzDocumentProviderApiFactory(
    {
      isJsonMime: () => true,
      basePath: env.documentContentBaseUrl,
    },
    undefined,
    axiosInstance
  );

  readonly documentProviderApi = papyrusMetadata.DocumentProviderApiFactory(
    {
      isJsonMime: () => true,
      basePath: env.documentContentBaseUrl,
    },
    undefined,
    axiosInstance
  );

  readonly signroomProviderApi = signroom.SignroomProviderApiFactory(
    {
      isJsonMime: () => true,
      basePath: env.documentContentBaseUrl,
    },
    undefined,
    axiosInstance
  );

  readonly adminConsoleApi = ConsoleDiAmministrazioneApiFactory(
    env.apiConfig,
    undefined,
    axiosInstance
  );

  readonly portafoglioApi = PortafoglioApiFactory(
    env.apiConfig,
    undefined,
    axiosInstance
  );

  readonly portafoglioZenithApi = PortfolioProviderApiFactory(
    {
      isJsonMime: () => true,
      basePath: env.owBaseUrl,
    },
    undefined,
    axiosInstance
  );

  readonly fondoPensioniApi = FondiPensioneApiFactory(
    env.apiConfig,
    undefined,
    axiosInstance
  );
  readonly azimutLifeApi = AzimutLifeApiFactory(
    env.apiConfig,
    undefined,
    axiosInstance
  );

  readonly documentApi = DocumentiApiFactory(
    env.apiConfig,
    undefined,
    axiosInstance
  );

  readonly anagraficaApi = anagrafica.AnagraficaApiFactory(
    {
      isJsonMime: () => true,
      basePath: env.anagraficaBaseUrl,
    },
    undefined,
    axiosInstance
  );

  readonly anagraficaCFApi = anagraficaCF.AnagraficaApiFactory(
    {
      isJsonMime: () => true,
      basePath: env.anagraficaBaseUrl,
    },
    undefined,
    axiosInstance
  );

  readonly documentContentApi = DocumentContentProviderApiFactory(
    {
      isJsonMime: () => true,
      basePath: env.documentContentBaseUrl,
    },
    undefined,
    axiosInstance
  );

  readonly marketplaceApi = MarketplaceApiFactory(
    env.apiConfig,
    undefined,
    axiosInstance
  );

  readonly prospectDocumentsApi = ProspectDocumentsApiFactory(
    {
      isJsonMime: () => true,
      basePath: env.baseUrl,
    },
    undefined,
    axiosInstance
  );

  readonly dobCustomerApi = dob.CustomerApiFactory(
    {
      isJsonMime: () => true,
      basePath: env.dobBaseUrl,
    },
    undefined,
    axiosInstance
  );

  readonly dobSurveyApi = dob.SurveyApiFactory(
    {
      isJsonMime: () => true,
      basePath: env.dobBaseUrl,
    },
    undefined,
    axiosInstance
  );

  readonly mifidApi = DefaultApiFactory(
    env.apiConfig,
    undefined,
    axiosInstance
  );

  readonly mifidApiPrecompile = MifidApiFactory(
    env.apiConfig,
    undefined,
    axiosInstance
  );

  readonly webCollaborationApi = WebCollaborationApiFactory(
    env.apiConfig,
    undefined,
    axiosInstance
  );

  readonly getQuestionnairesStatus = StatoQuestionariApiFactory(
    env.apiConfig,
    undefined,
    axiosInstance
  );

  readonly updateQuestionnairesStatus = StatoQuestionariApiFactory(
    env.apiConfig,
    undefined,
    axiosInstance
  );

  readonly tasksApi = TasksApiFactory(env.apiConfig, undefined, axiosInstance);
  readonly signingApi = SigningApiFactory(
    env.apiConfig,
    undefined,
    axiosInstance
  );

  readonly onboardingServizioApi = dob.ServizioApiFactory(
    {
      isJsonMime: () => true,
      basePath: env.dobBaseUrl,
    },
    undefined,
    axiosInstance
  );

  readonly prometeiaCustomersApi = prometeia.CustomersControllerApiFactory(
    {
      isJsonMime: () => true,
      basePath: env.prometeiaBaseUrl,
    },
    undefined,
    axiosInstance
  );

  readonly prometeiaFormApi = prometeia.FormControllerApiFactory(
    {
      isJsonMime: () => true,
      basePath: env.prometeiaBaseUrl,
    },
    undefined,
    axiosInstance
  );

  readonly prometeiaCustomersV2Api = prometeia.CustomersControllerV2ApiFactory(
    {
      isJsonMime: () => true,
      basePath: env.prometeiaBaseUrl,
    },
    undefined,
    axiosInstance
  );

  readonly prometeiaGovernanceApi = prometeia.GovernanceControllerApiFactory(
    {
      isJsonMime: () => true,
      basePath: env.prometeiaBaseUrl,
    },
    undefined,
    axiosInstance
  );

  readonly amlSurveyApi = AmlTemplateApiFactory(
    env.apiConfig,
    undefined,
    axiosInstance
  );

  readonly dobUtilityApi = dob.UtilityApiFactory(
    {
      isJsonMime: () => true,
      basePath: env.dobBaseUrl,
    },
    undefined,
    axiosInstance
  );

  readonly pirBoxApi = PirBoxProviderApiFactory(
    {
      isJsonMime: () => true,
      basePath: env.owBaseUrl,
    },
    undefined,
    axiosInstance
  );
  readonly registrationProviderApi = RegistrationProviderApiFactory(
    {
      isJsonMime: () => true,
      basePath: env.owBaseUrl,
    },
    undefined,
    axiosInstance
  );
  readonly regDispApi = DefaultApiFactory(
    env.apiConfig,
    undefined,
    axiosInstance
  );

  readonly pirBoxPortafoglio = EnginePortafoglioApiFactory(
    {
      isJsonMime: () => true,
      basePath: env.anagraficaBaseUrl,
    },
    undefined,
    axiosInstance
  );

  readonly documentale = documentale.DocumentaleApiFactory(
    {
      isJsonMime: () => true,
      basePath: env.dobBaseUrl,
    },
    undefined,
    axiosInstance
  );

  readonly media = MediaApiFactory(env.apiConfig, undefined, axiosInstance);

  readonly agentsApi = AgentsApiFactory(
    env.apiConfig,
    undefined,
    axiosInstance
  );

  readonly config = ConfigApiFactory(env.apiConfig, undefined, axiosInstance);

  readonly documentAcceptanceApi = DocumentAcceptanceApiFactory(
    env.apiConfig,
    undefined,
    axiosInstance
  );
}

const apiManager = new ApiManager();
export default apiManager;
